import { useTranslation } from "@/src/i18n/client";
import React from "react";
import { SparkleIcon } from "@phosphor-icons/react";
import { Button } from "@/src/components/ui/button";

interface AnalyzeButtonProps {
  onClick: () => void;
  disabled: boolean;
  uploading: boolean;
  analyzing: boolean;
}

export function AnalyzeButton({
  onClick,
  disabled,
  uploading,
  analyzing
}: AnalyzeButtonProps) {
  const { t } = useTranslation();
  const [hovered, setHovered] = React.useState(false);

  // Only show moving glow when analyzing, but show static outside glow on hover
  const showGlow = analyzing;
  const showStaticGlow = hovered && !analyzing;

  return (
    <div className="relative mt-6 w-full">
      <Button
        type="button"
        className="w-full rounded-lg px-5 py-2.5 text-center text-sm font-medium flex items-center justify-center gap-2 relative transition-all duration-300 overflow-hidden"
        onClick={onClick}
        disabled={disabled || uploading || analyzing}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        variant="default"
      >
        <SparkleIcon className="w-5 h-5 mr-1 relative z-20" />
        <span className="relative z-20">
          {uploading
            ? t('homePage.uploading')
            : analyzing
              ? `${t('homePage.analyzing')}`
              : `${t('homePage.analyzeButton')}`}
        </span>
        {/* Moving glow when analyzing */}
        {showGlow && (
          <span
            className="pointer-events-none absolute inset-0 z-10 transition-opacity duration-300"
            aria-hidden="true"
          >
            <span className="analyze-glow block absolute inset-0" />
          </span>
        )}
        {/* Static glow on hover */}
        {showStaticGlow && (
          <span
            className="pointer-events-none absolute inset-0 z-10 transition-opacity duration-500"
            aria-hidden="true"
          >
            <span className="analyze-static-glow block absolute inset-0" />
          </span>
        )}
      </Button>
      <style>{`
        .analyze-glow {
          background: linear-gradient(
            120deg,
            rgba(255,255,255,0) 0%,
            rgba(255,255,255,0.3) 40%,
            rgba(255,255,255,0.8) 50%,
            rgba(255,255,255,0.3) 60%,
            rgba(255,255,255,0) 100%
          );
          filter: blur(8px);
          width: 200%;
          height: 200%;
          left: -50%;
          top: -50%;
          position: absolute;
          animation: analyze-glow-move 1.5s ease-in-out infinite;
          opacity: 1;
          transition: opacity 0.3s ease-in-out;
        }
        .dark .analyze-glow {
          background: linear-gradient(
            120deg,
            rgba(147, 51, 234, 0) 0%,
            rgba(168, 85, 247, 0.5) 35%,
            rgba(192, 132, 252, 0.9) 45%,
            rgba(233, 213, 255, 1) 50%,
            rgba(192, 132, 252, 0.9) 55%,
            rgba(168, 85, 247, 0.5) 65%,
            rgba(147, 51, 234, 0) 100%
          );
          filter: blur(12px);
        }
        .analyze-static-glow {
          background: radial-gradient(circle, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.15) 60%, rgba(255,255,255,0) 100%);
          filter: blur(10px);
          width: 100%;
          height: 100%;
          left: 0;
          top: 0;
          position: absolute;
          pointer-events: none;
          opacity: 1;
          transition: opacity 0.5s ease-in-out;
        }
        .dark .analyze-static-glow {
          background: radial-gradient(
            circle,
            rgba(168, 85, 247, 0.7) 0%,
            rgba(147, 51, 234, 0.5) 40%,
            rgba(126, 34, 206, 0.3) 70%,
            rgba(107, 33, 168, 0) 100%
          );
          filter: blur(20px);
        }
        @keyframes analyze-glow-move {
          0% {
            left: -100%;
            opacity: 0.5;
          }
          50% {
            opacity: 1;
          }
          100% {
            left: 50%;
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
}
